import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Calendar, Brain, Award, Sparkles, ExternalLink, ChevronRight, CheckCircle2, AlertCircle, FileText, Download } from "lucide-react";
import { getTheme, fonts } from "../theme";
import Navbar from "../components/Navbar";
import TrustDial from "../components/TrustDial";
import BreakdownGrid from "../components/BreakdownGrid";
import { EMPLOYEE_CRITERIA } from "../criteria";
import { Button, Field, inputStyle } from "../components/UI";
import api from "../api";
import {
  candidateRowId,
  wibilEmployeeIdFromRow,
  namesMatch,
  samePersonName,
  mergeSamePersonCandidates,
  buildLocalCandidateReport,
  linkedInPeopleSearchUrl,
  githubPeopleSearchUrl,
  isDummyCandidate,
  candidateDisplayPhotoSrc,
  candidateInitials,
  displayPersonName,
  displayCompanyName,
  persistLocalShortlist,
  normalizeLiveCandidate
} from "../utils/recruiterUtils";
import {
  persistCandidateForProfile,
  readPersistedCandidate,
  clearPersistedCandidate
} from "../candidateProfileNav";

function crumbStyle(theme, { current, accent } = {}) {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 14px",
    borderRadius: 8,
    border: current ? "none" : `1px solid ${theme.line}`,
    background: current ? theme.accent : "transparent",
    color: current ? "#fff" : (accent ? theme.accent : theme.text),
    fontWeight: 600,
    fontSize: 13,
    textDecoration: "none",
    cursor: current ? "default" : "pointer"
  };
}

function fallbackNameFromCandidateId(candidate, urlId) {
  const raw = String(candidate?.id || candidate?.employeeId || urlId || "");
  const decoded = decodeURIComponent(raw);
  const slug = decoded
    .replace(/^li_in_/i, "")
    .replace(/^li_/i, "")
    .replace(/^gh_/i, "")
    .replace(/^live_gh_/i, "")
    .replace(/^naukri_/i, "")
    .replace(/_\d+$/, "")
    .replace(/[-_]+/g, " ")
    .trim();
  if (!slug || /^\d+$/.test(slug)) return "";
  return slug.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatInterviewLabel(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value).replace("T", " ");
  return dt.toLocaleString();
}

export default function CandidateProfile({ onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("recruiter", themeMode);
  const navigate = useNavigate();
  const location = useLocation();
  const { candidateId } = useParams();

  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateReport, setCandidateReport] = useState(null);
  const [isWibilRegistered, setIsWibilRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resumeUrl, setResumeUrl] = useState("");

  const [resumeText, setResumeText] = useState("");
  const [targetJobRoleScreen, setTargetJobRoleScreen] = useState("");
  const [targetJobPosition, setTargetJobPosition] = useState("");
  const [matchResult, setMatchResult] = useState(null);
  const [interviewDate, setInterviewDate] = useState("");
  const [schedulingStatus, setSchedulingStatus] = useState("");
  const [screenBusy, setScreenBusy] = useState(false);
  const [shortlistBusy, setShortlistBusy] = useState(false);

  const candidateName = selectedCandidate
    ? (displayPersonName(selectedCandidate) || selectedCandidate.name || candidateReport?.profile?.name || fallbackNameFromCandidateId(selectedCandidate, candidateId))
    : (displayPersonName(candidateReport?.profile) || candidateReport?.profile?.name || fallbackNameFromCandidateId(null, candidateId));
  const candidateCompany = selectedCandidate
    ? displayCompanyName(selectedCandidate) || displayCompanyName(candidateReport?.profile)
    : displayCompanyName(candidateReport?.profile);
  const candidatePhoto = selectedCandidate ? (candidateDisplayPhotoSrc(selectedCandidate) || selectedCandidate.avatar_url) : "";

  const candidatePlatformStr = String(
    selectedCandidate?.platform || selectedCandidate?.source || selectedCandidate?.badgeText || ""
  ).toLowerCase();

  const isLinkedIn = Boolean(
    candidatePlatformStr.includes("linkedin") ||
    String(selectedCandidate?.id || "").startsWith("li_") ||
    String(selectedCandidate?.id || "").startsWith("urn:li:")
  );

  const isGitHub = Boolean(
    candidatePlatformStr.includes("github") ||
    String(selectedCandidate?.id || "").startsWith("gh_") ||
    String(selectedCandidate?.id || "").startsWith("live_gh_")
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [me, jobsList] = await Promise.all([
          api.get("/recruiter/profile"),
          api.get("/recruiter/jobs")
        ]);
        if (cancelled) return;
        setProfile(me);
        const list = Array.isArray(jobsList) ? jobsList : [];
        setJobs(list);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load recruiter profile.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fromState = location.state?.candidate;
    const fromStore = readPersistedCandidate();
    const clicked = fromState || fromStore;
    const urlId = candidateId ? decodeURIComponent(candidateId) : "";

    if (!urlId) {
      setSelectedCandidate(null);
      setCandidateReport(null);
      setMatchResult(null);
      setSchedulingStatus("");
      return;
    }

    if (clicked && String(candidateRowId(clicked) || clicked.name || "") === urlId) {
      loadCandidate(clicked);
      return;
    }
    if (clicked) {
      loadCandidate(clicked);
      return;
    }
    if (/^\d+$/.test(urlId)) {
      loadCandidate({ id: urlId, employeeId: urlId, name: "" });
      return;
    }
    loadCandidate({
      id: urlId,
      name: fallbackNameFromCandidateId({ id: urlId }, urlId),
      platform: String(urlId).startsWith("li_") || String(urlId).startsWith("urn:li:")
        ? "LinkedIn"
        : (String(urlId).startsWith("gh_") || String(urlId).startsWith("live_gh_") ? "GitHub" : "Live Search")
    });
  }, [candidateId, location.state]);

  const loadCandidate = async (clicked) => {
    if (isDummyCandidate(clicked)) {
      setSelectedCandidate(null);
      setCandidateReport(null);
      return;
    }
    setError("");
    setSelectedCandidate(clicked);
    persistCandidateForProfile(clicked);
    setMatchResult(null);
    setSchedulingStatus("");
    setResumeText(""); // Start empty as requested for candidate resume/profile details

    const initialReport = buildLocalCandidateReport(clicked);
    setCandidateReport(initialReport);

    if (clicked.resume_url || clicked.resume) {
      setResumeUrl(clicked.resume_url || clicked.resume);
    }

    const sourcedId = clicked.id || clicked.candidateId;
    if (sourcedId && /^\d+$/.test(String(sourcedId))) {
      try {
        const sourced = await api.getSourcedCandidate(sourcedId);
        if (sourced?.candidate) {
          clicked = normalizeLiveCandidate({ ...clicked, ...sourced.candidate });
          setSelectedCandidate(clicked);
          persistCandidateForProfile(clicked);
          setCandidateReport(buildLocalCandidateReport(clicked));
        }
      } catch (err) {
        /* id may belong to a WIBIL employee instead */
      }
    }

    // Check if candidate is registered in WIBIL through email, ID, or employee lookup
    const rawEmail = String(clicked.email || "").trim().toLowerCase();
    const candidateNameStr = String(clicked.name || clicked.candidate_name || "").trim().toLowerCase();
    let isReg = Boolean(clicked.wibil_user_id || clicked.employee_id || clicked.is_registered || clicked.is_wibil_user);
    let matchedEmail = rawEmail;

    try {
      const candidatesList = await api.getAppliedCandidates(rawEmail || candidateNameStr);
      const list = mergeSamePersonCandidates(Array.isArray(candidatesList) ? candidatesList : []);
      const match = list.find(c => {
        const cEmail = String(c.email || "").trim().toLowerCase();
        const cName = String(c.name || c.candidate_name || "").trim();
        if (rawEmail && cEmail === rawEmail) return true;
        if (candidateNameStr && samePersonName(cName, candidateNameStr)) return true;
        return false;
      });

      if (match) {
        isReg = true;
        const allEmails = [match.email, ...(Array.isArray(match.email_aliases) ? match.email_aliases : [])]
          .map((e) => String(e || "").trim())
          .filter(Boolean);
        matchedEmail = [...new Set(allEmails.map((e) => e.toLowerCase()))]
          .map((e) => allEmails.find((x) => x.toLowerCase() === e) || e)
          .join(" · ") || rawEmail || `${candidateNameStr.replace(/\s+/g, ".")}@registered.wibil.co`;
        if (match.resume_url) setResumeUrl(match.resume_url);
        if (match.resume || match.summary || match.strengths) {
          const regResume = [
            `Candidate Name: ${match.name}`,
            `Email: ${matchedEmail}`,
            match.title ? `Role: ${match.title}` : "",
            match.company ? `Company: ${match.company}` : "",
            match.summary ? `Professional Summary: ${match.summary}` : "",
            match.strengths ? `Key Skills: ${match.strengths}` : "",
            match.experience ? `Experience: ${match.experience}` : ""
          ].filter(Boolean).join("\n\n");
          setResumeText(regResume);
        }
      }
    } catch (err) {
      console.warn("Candidate registration check error:", err);
    }

    setIsWibilRegistered(isReg);
    setRegisteredEmail(matchedEmail);

    const employeeId = wibilEmployeeIdFromRow(clicked);
    if (!employeeId) return;
    try {
      const report = await api.get(`/recruiter/candidate/${encodeURIComponent(employeeId)}`);
      const reportName = report?.profile?.name;
      const clickedName = clicked.name || clicked.candidate_name || clicked.displayName;
      if (clickedName && reportName && !namesMatch(reportName, clickedName)) return;
      setCandidateReport({
        ...report,
        profile: {
          ...report.profile,
          name: displayPersonName(clicked) || clickedName || report.profile.name,
          title: displayCompanyName(clicked) || clicked.job_role || report.profile.title,
          current_company: displayCompanyName({ ...report.profile, ...clicked })
        }
      });
      if (report?.profile?.resume_url) {
        setResumeUrl(report.profile.resume_url);
      }
      if (report?.profile?.email) {
        setIsWibilRegistered(true);
        setRegisteredEmail((prev) => {
          const extra = String(report.profile.email || "").trim();
          if (!extra) return prev;
          const parts = String(prev || "").split("·").map((s) => s.trim()).filter(Boolean);
          if (parts.some((p) => p.toLowerCase() === extra.toLowerCase())) return prev || extra;
          return [...parts, extra].join(" · ");
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const goEmptyProfile = () => {
    clearPersistedCandidate();
    setSelectedCandidate(null);
    setCandidateReport(null);
    setMatchResult(null);
    setSchedulingStatus("");
    navigate("/recruiter/candidate-profile");
  };

  const runResumeScreening = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!resumeText.trim()) {
      setError("Please paste the Candidate Resume text or summary to analyze.");
      return;
    }
    if (!targetJobRoleScreen.trim()) {
      setError("Please enter the Job Position / Target Skills to match against.");
      return;
    }
    setError("");
    setScreenBusy(true);
    try {
      const skillsArray = targetJobRoleScreen.split(/[,/| ]+/).filter(Boolean);
      const screening = await api.post("/ai/resume-analyzer", {
        resume_text: resumeText,
        job_keywords: skillsArray
      });
      setMatchResult(screening);
      if (!targetJobPosition.trim()) setTargetJobPosition(targetJobRoleScreen.trim());
    } catch (err) {
      setError(err.message || "Failed to screen resume.");
    } finally {
      setScreenBusy(false);
    }
  };

  const submitShortlist = async ({ schedule } = { schedule: false }) => {
    const positionName = targetJobPosition.trim() || targetJobRoleScreen.trim();
    if (!positionName) {
      setError("Please enter the Target Job Position.");
      return;
    }
    if (schedule && !interviewDate) {
      setError("Please select an Interview Date & Time to schedule.");
      return;
    }
    const resolvedName = candidateName || fallbackNameFromCandidateId(selectedCandidate, candidateId) || "Sourced Candidate";
    const interviewLabel = interviewDate ? formatInterviewLabel(interviewDate) : "";
    setError("");
    setSchedulingStatus("");
    setShortlistBusy(true);
    try {
      if (!targetJobPosition.trim()) setTargetJobPosition(positionName);
      const shortlistEmployeeId = wibilEmployeeIdFromRow(selectedCandidate);
      const matchedJob = jobs.find(j => String(j.role || j.title || "").toLowerCase() === positionName.toLowerCase());
      const jobIdToUse = matchedJob ? matchedJob.id : (jobs[0] ? jobs[0].id : null);
      const platform = isLinkedIn ? "LinkedIn" : (isGitHub ? "GitHub" : (selectedCandidate?.platform || selectedCandidate?.source || "Live Search"));
      const company = candidateCompany || selectedCandidate?.company || selectedCandidate?.current_company || "";

      let backendLinkId = null;
      let backendEmpId = shortlistEmployeeId || null;
      let apiError = "";
      try {
        const res = await api.post("/recruiter/candidates/shortlist", {
          job_id: jobIdToUse,
          employee_id: shortlistEmployeeId || null,
          candidate_name: resolvedName,
          job_role: positionName,
          company,
          platform,
          interview_date: interviewDate || "",
          feedback: interviewLabel
            ? `Shortlisted for "${positionName}" · Interview ${interviewLabel}`
            : `Shortlisted for "${positionName}"`
        });
        backendLinkId = res?.link_id || res?.linkId || null;
        if (res?.employee_id) backendEmpId = res.employee_id;
      } catch (postErr) {
        apiError = postErr.message || "Server sync failed.";
      }

      persistLocalShortlist({
        link_id: backendLinkId || Date.now(),
        employee_id: backendEmpId || null,
        candidate_id: candidateRowId(selectedCandidate) || candidateId,
        candidate_name: resolvedName,
        name: resolvedName,
        job_role: positionName,
        status: "shortlisted",
        interview_date: interviewLabel || "Not scheduled",
        match_score: matchResult?.match_score_pct || matchResult?.match_score || matchResult?.score || 85,
        feedback: interviewLabel
          ? `Shortlisted for "${positionName}" · Interview ${interviewLabel}`
          : `Shortlisted for "${positionName}"`,
        company,
        platform
      });

      if (apiError) {
        setError(`${apiError} The candidate was still saved to your Recruiter Dashboard on this browser.`);
      }
      setSchedulingStatus(
        interviewLabel
          ? `${resolvedName} shortlisted for "${positionName}". Interview scheduled for ${interviewLabel}.`
          : `${resolvedName} shortlisted for "${positionName}".`
      );
    } catch (err) {
      setError(err.message || "Failed to shortlist candidate.");
    } finally {
      setShortlistBusy(false);
    }
  };

  const handleShortlist = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await submitShortlist({ schedule: true });
  };

  const selectedCandidateId = selectedCandidate ? (candidateRowId(selectedCandidate) || selectedCandidate.name) : null;

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
      <Navbar theme={theme} userName={profile?.name} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "20px 20px 48px" }}>
        {/* Navigation Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          style={{
            marginBottom: 16,
            padding: 6,
            background: theme.surface,
            border: `1px solid ${theme.line}`,
            borderRadius: 12
          }}
        >
          <ol style={{ display: "flex", alignItems: "center", gap: 4, listStyle: "none", margin: 0, padding: 0, flexWrap: "wrap" }}>
            <li>
              <Link to="/recruiter" style={crumbStyle(theme)}>Recruiter Hub</Link>
            </li>
            <li style={{ display: "flex", alignItems: "center", color: theme.textMute }}>
              <ChevronRight size={16} aria-hidden="true" />
            </li>
            <li>
              <Link to="/recruiter#matching-candidates" style={crumbStyle(theme)}>Searched Candidates</Link>
            </li>
            <li style={{ display: "flex", alignItems: "center", color: theme.textMute }}>
              <ChevronRight size={16} aria-hidden="true" />
            </li>
            <li>
              {selectedCandidateId ? (
                <Link to="/recruiter/candidate-profile" onClick={goEmptyProfile} style={crumbStyle(theme, { accent: true })}>
                  Candidate Profile
                </Link>
              ) : (
                <span style={crumbStyle(theme, { current: true })} aria-current="page">Candidate Profile</span>
              )}
            </li>
            {selectedCandidateId && candidateName && (
              <>
                <li style={{ display: "flex", alignItems: "center", color: theme.textMute }}>
                  <ChevronRight size={16} aria-hidden="true" />
                </li>
                <li>
                  <span style={crumbStyle(theme, { current: true })} aria-current="page">{candidateName}</span>
                </li>
              </>
            )}
          </ol>
        </nav>

        {error && (
          <div className="p-3 rounded-3 mb-3 small fw-semibold" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "#EF4444" }}>
            {error}
          </div>
        )}

        {!selectedCandidateId ? (
          <div style={{ background: theme.surface, border: `1px dashed ${theme.line}`, borderRadius: 12, padding: "48px 24px", textAlign: "center", color: theme.textMute, fontSize: 14 }}>
            <div style={{ marginBottom: 14 }}>
              Select a candidate from the Recruiter Hub to view their resume, run AI predictions, and schedule interviews.
            </div>
            <Link
              to="/recruiter#matching-candidates"
              style={{ color: theme.accent, fontWeight: 700, fontSize: 13, textDecoration: "none" }}
            >
              Open Searched Candidates →
            </Link>
          </div>
        ) : candidateReport === null ? (
          <div style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 12, padding: 40, textAlign: "center", color: theme.textMute }}>
            Loading Candidate Profile &amp; Verification Details...
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

            {/* Candidate Header Card */}
            <div style={{ background: theme.surface, border: `1px solid ${theme.line}`, borderRadius: 16, padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                    padding: "4px 8px 2px",
                    borderRadius: 12,
                    background: theme.bg,
                    border: `1px solid ${theme.line}`,
                    minWidth: 110
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 11, color: theme.textMute, marginBottom: 2, letterSpacing: 0.3 }}>
                      Trust Score
                    </div>
                    <TrustDial
                      score={
                        typeof candidateReport?.trust_score === "object"
                          ? (candidateReport?.trust_score?.trust_score ?? 650)
                          : (candidateReport?.trust_score ?? selectedCandidate?.trust_score ?? 650)
                      }
                      theme={theme}
                      size={96}
                    />
                    {isWibilRegistered && Number(candidateReport?.trust_score?.rating_count || selectedCandidate?.rating_count || 0) > 0 && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: "#10B981", marginTop: 2, textAlign: "center" }}>
                        {Number(candidateReport?.trust_score?.rating_count || selectedCandidate?.rating_count)} rating
                        {Number(candidateReport?.trust_score?.rating_count || selectedCandidate?.rating_count) === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                  <div style={{ position: "relative", width: 64, height: 64, flexShrink: 0 }}>
                    <div style={{
                      width: 64, height: 64, borderRadius: "50%",
                      background: theme.accentDim, color: theme.accent,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 700, fontSize: 20, border: `1px solid ${theme.line}`
                    }}>
                      {candidateInitials(selectedCandidate)}
                    </div>
                    {candidatePhoto ? (
                      <img
                        src={candidatePhoto}
                        alt=""
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        style={{
                          position: "absolute", inset: 0, width: 64, height: 64,
                          borderRadius: "50%", objectFit: "cover",
                          border: `1px solid ${theme.line}`, display: "block", background: theme.bg
                        }}
                      />
                    ) : null}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <h2 style={{ fontFamily: fonts.display, fontSize: 22, fontWeight: 800, margin: 0, color: theme.text }}>
                        {candidateName || "Candidate Profile"}
                      </h2>
                      {isWibilRegistered ? (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          fontSize: 11, fontWeight: 700, background: "rgba(16,185,129,0.15)",
                          color: "#10B981", border: "1px solid rgba(16,185,129,0.3)",
                          padding: "3px 10px", borderRadius: 99
                        }}>
                          <CheckCircle2 size={13} /> WIBIL Registered &amp; Verified
                        </span>
                      ) : (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          fontSize: 11, fontWeight: 700, background: "rgba(245,158,11,0.15)",
                          color: "#F59E0B", border: "1px solid rgba(245,158,11,0.3)",
                          padding: "3px 10px", borderRadius: 99
                        }}>
                          <AlertCircle size={13} /> Sourced Candidate ({isLinkedIn ? "LinkedIn" : (isGitHub ? "GitHub" : (selectedCandidate?.platform || selectedCandidate?.source || "Live Search"))})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13.5, color: theme.textMute, marginTop: 4 }}>
                      {[
                        candidateCompany || selectedCandidate?.job_role || selectedCandidate?.title,
                        selectedCandidate?.location || null,
                        (selectedCandidate?.experience || selectedCandidate?.total_experience)
                          ? `Exp: ${selectedCandidate?.experience || selectedCandidate?.total_experience}`
                          : null,
                        registeredEmail ? `Email: ${registeredEmail}` : (selectedCandidate?.email ? `Email: ${selectedCandidate.email}` : null),
                        selectedCandidate?.phone ? `Phone: ${selectedCandidate.phone}` : null
                      ].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>

                {/* External links & Resume Download */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  {resumeUrl && (
                    <a
                      href={resumeUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 12.5, color: "#fff", background: "#10B981", display: "inline-flex", alignItems: "center", gap: 6,
                        fontWeight: 700, borderRadius: 8, padding: "7px 14px", textDecoration: "none"
                      }}
                    >
                      <Download size={14} /> Download Resume
                    </a>
                  )}

                  {/* Show LinkedIn Search only if LinkedIn */}
                  {isLinkedIn && (
                    <a
                      href={selectedCandidate?.profileUrl || selectedCandidate?.linkedin_url || linkedInPeopleSearchUrl(selectedCandidate)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 12, color: "#0A66C2", display: "inline-flex", alignItems: "center", gap: 6,
                        fontWeight: 700, border: "1px solid rgba(10,102,194,0.3)", borderRadius: 8, padding: "6px 12px",
                        textDecoration: "none", background: "rgba(10,102,194,0.08)"
                      }}
                    >
                      <ExternalLink size={13} /> LinkedIn Search
                    </a>
                  )}

                  {/* Show GitHub Search only if GitHub */}
                  {isGitHub && (
                    <a
                      href={selectedCandidate?.profileUrl || selectedCandidate?.github_url || githubPeopleSearchUrl(selectedCandidate)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 12, color: theme.text, display: "inline-flex", alignItems: "center", gap: 6,
                        fontWeight: 600, border: `1px solid ${theme.line}`, borderRadius: 8, padding: "6px 12px",
                        textDecoration: "none", background: theme.bg
                      }}
                    >
                      <ExternalLink size={13} /> GitHub Search
                    </a>
                  )}

                  {!isLinkedIn && !isGitHub && !isWibilRegistered && (
                    <a
                      href={selectedCandidate?.profileUrl || linkedInPeopleSearchUrl(selectedCandidate)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 12, color: "#0A66C2", display: "inline-flex", alignItems: "center", gap: 6,
                        fontWeight: 700, border: "1px solid rgba(10,102,194,0.3)", borderRadius: 8, padding: "6px 12px",
                        textDecoration: "none", background: "rgba(10,102,194,0.08)"
                      }}
                    >
                      <ExternalLink size={13} /> Profile Link
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* PART 1: AI Resume Prediction & Matcher */}
            <div style={{
              background: theme.surface,
              border: `1px solid ${theme.line}`,
              borderRadius: 16,
              padding: 24,
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: "rgba(59, 130, 246, 0.15)", display: "flex",
                  alignItems: "center", justifyContent: "center"
                }}>
                  <Brain size={20} color="#3B82F6" />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: theme.text }}>
                    Part 1: Candidate Resume &amp; AI Prediction Matcher
                  </h3>
                  <p style={{ color: theme.textMute, fontSize: 12, margin: 0 }}>
                    {isWibilRegistered
                      ? "Fetched candidate resume details from WIBIL verified profile."
                      : "Paste candidate resume details below and run AI match against target job requirements."}
                  </p>
                </div>
              </div>

              {resumeUrl && (
                <div style={{
                  margin: "14px 0 16px",
                  padding: "12px 16px",
                  borderRadius: 10,
                  background: "rgba(16,185,129,0.1)",
                  border: "1px solid rgba(16,185,129,0.25)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 10
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FileText size={18} color="#10B981" />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: theme.text }}>Official Candidate Resume Document Attached</div>
                      <div style={{ fontSize: 11.5, color: theme.textMute }}>Verified resume file from candidate registration</div>
                    </div>
                  </div>
                  <a
                    href={resumeUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontSize: 12, fontWeight: 700, color: "#10B981", textDecoration: "none",
                      display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(16,185,129,0.15)",
                      padding: "5px 12px", borderRadius: 6
                    }}
                  >
                    <Download size={13} /> View / Download Document
                  </a>
                </div>
              )}

              <form onSubmit={runResumeScreening} style={{ marginTop: 14 }}>
                <Field label="Candidate Resume / Profile Details" theme={theme}>
                  <textarea
                    required
                    style={{
                      ...inputStyle(theme),
                      minHeight: 120,
                      resize: "vertical",
                      lineHeight: 1.5,
                      borderRadius: 10
                    }}
                    value={resumeText}
                    onChange={e => setResumeText(e.target.value)}
                    placeholder="Paste candidate resume text, work experience, certifications, and technical skills..."
                  />
                </Field>

                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "flex-end" }}>
                  <Field label="Target Job Position / Required Skills (Textbox)" theme={theme}>
                    <input
                      type="text"
                      required
                      style={{ ...inputStyle(theme), borderRadius: 10 }}
                      value={targetJobRoleScreen}
                      onChange={e => setTargetJobRoleScreen(e.target.value)}
                      placeholder="e.g. Full Stack Developer (React, Node.js, Python, PostgreSQL)"
                    />
                  </Field>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="submit" theme={theme} style={{ padding: "10px 22px", fontWeight: 700, borderRadius: 10 }} disabled={screenBusy}>
                      <Sparkles size={16} /> {screenBusy ? "Predicting..." : "Run AI Prediction"}
                    </Button>
                  </div>
                </div>
              </form>

              {matchResult && (
                <div style={{
                  background: theme.bg,
                  border: `1px solid ${theme.line}`,
                  borderRadius: 12,
                  padding: 20,
                  marginTop: 18
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${theme.line}` }}>
                    <div>
                      <span style={{ fontWeight: 800, fontSize: 15, color: theme.text }}>AI Hiring Match Prediction</span>
                      <div style={{ fontSize: 11, color: theme.textMute, marginTop: 2 }}>Automated neural match analysis</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 24, fontWeight: 900, color: (matchResult.match_score_pct || matchResult.match_score || matchResult.score || 80) >= 70 ? "#10B981" : "#3B82F6" }}>
                        {matchResult.match_score_pct || matchResult.match_score || matchResult.score || 85}%
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        padding: "4px 10px", borderRadius: 6,
                        background: (matchResult.match_score_pct || matchResult.match_score || 80) >= 70 ? "rgba(16,185,129,0.15)" : "rgba(59,130,246,0.15)",
                        color: (matchResult.match_score_pct || matchResult.match_score || 80) >= 70 ? "#10B981" : "#3B82F6"
                      }}>
                        {matchResult.recommendation || ((matchResult.match_score_pct || 80) >= 70 ? "Strong Candidate" : "Review Candidate")}
                      </span>
                    </div>
                  </div>

                  <div style={{ marginBottom: 12, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: theme.textMute, marginBottom: 6 }}>
                      Matched Keywords &amp; Competencies ({matchResult.matched_keywords?.length || 0})
                    </div>
                    {matchResult.matched_keywords?.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {matchResult.matched_keywords.map((kw, i) => (
                          <span key={i} style={{ background: "rgba(16,185,129,0.12)", color: "#10B981", fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 6 }}>
                            ✓ {kw}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: theme.textMute, fontSize: 12 }}>None matched</span>
                    )}
                  </div>

                  {matchResult.missing_keywords?.length > 0 && (
                    <div style={{ marginBottom: 12, fontSize: 13 }}>
                      <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: theme.textMute, marginBottom: 6 }}>
                        Skill Gap / Missing Keywords ({matchResult.missing_keywords.length})
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {matchResult.missing_keywords.map((kw, i) => (
                          <span key={i} style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444", fontSize: 11.5, fontWeight: 600, padding: "3px 9px", borderRadius: 6 }}>
                            ✕ {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {matchResult.summary && (
                    <p style={{ fontSize: 13, color: theme.text, margin: "10px 0 0", lineHeight: 1.5 }}>
                      <strong>AI Summary &amp; Prediction:</strong> {matchResult.summary}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* PART 2: Shortlist & Schedule Interview (Textbox for Position) */}
            <div style={{
              background: theme.surface,
              border: `1px solid ${theme.line}`,
              borderRadius: 16,
              padding: 24,
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: "rgba(16, 185, 129, 0.15)", display: "flex",
                  alignItems: "center", justifyContent: "center"
                }}>
                  <Calendar size={20} color="#10B981" />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: theme.text }}>
                    Part 2: Shortlist &amp; Schedule Interview
                  </h3>
                  <p style={{ color: theme.textMute, fontSize: 12, margin: 0 }}>
                    Enter the job position, then shortlist the candidate or schedule an interview.
                  </p>
                </div>
              </div>

              {schedulingStatus && (
                <div style={{
                  background: "rgba(16,185,129,0.15)",
                  border: "1px solid rgba(16,185,129,0.3)",
                  color: "#10B981",
                  padding: "14px 18px",
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 700,
                  marginTop: 14,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CheckCircle2 size={18} /> {schedulingStatus}
                  </div>
                  <div>
                    <Button
                      type="button"
                      theme={theme}
                      onClick={() => navigate("/recruiter")}
                      style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700 }}
                    >
                      View in Recruiter Dashboard &rarr;
                    </Button>
                  </div>
                </div>
              )}

              <form onSubmit={handleShortlist} style={{ marginTop: 18 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <Field label="Target Job Position (Textbox)" theme={theme}>
                    <input
                      type="text"
                      style={{ ...inputStyle(theme), borderRadius: 10 }}
                      value={targetJobPosition}
                      onChange={e => setTargetJobPosition(e.target.value)}
                      placeholder="e.g. Senior Full Stack Engineer / Tech Lead"
                    />
                  </Field>

                  <Field label="Interview Date & Time" theme={theme}>
                    <input
                      type="datetime-local"
                      style={{ ...inputStyle(theme), borderRadius: 10 }}
                      value={interviewDate}
                      onChange={e => setInterviewDate(e.target.value)}
                    />
                  </Field>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
                  <Button
                    type="button"
                    theme={theme}
                    onClick={() => submitShortlist({ schedule: false })}
                    style={{ width: "100%", justifyContent: "center", borderRadius: 10, padding: "12px 18px", fontWeight: 700 }}
                    disabled={shortlistBusy}
                  >
                    <CheckCircle2 size={16} /> {shortlistBusy ? "Saving..." : "Shortlist Candidate"}
                  </Button>
                  <Button
                    type="submit"
                    theme={theme}
                    style={{ width: "100%", justifyContent: "center", borderRadius: 10, padding: "12px 18px", fontWeight: 700 }}
                    disabled={shortlistBusy}
                  >
                    <Calendar size={16} /> {shortlistBusy ? "Saving..." : "Schedule Interview"}
                  </Button>
                </div>
              </form>
            </div>

            {/* PART 3: Certifications, Verified Skills & Credentials */}
            <div style={{
              background: theme.surface,
              border: `1px solid ${theme.line}`,
              borderRadius: 16,
              padding: 24,
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: "rgba(245, 158, 11, 0.15)", display: "flex",
                  alignItems: "center", justifyContent: "center"
                }}>
                  <Award size={20} color="#F59E0B" />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: theme.text }}>
                    Part 3: Certifications, Verified Skills &amp; Credentials
                  </h3>
                  <p style={{ color: theme.textMute, fontSize: 12, margin: 0 }}>
                    Platform activity, verified technical skills, and compliance credentials.
                  </p>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16, marginTop: 16 }}>
                <div style={{ padding: "18px 20px", borderRadius: 12, background: theme.bg, border: `1px solid ${theme.line}`, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: theme.accent, marginBottom: 8 }}>
                      Sourced Platform Credentials
                    </div>
                    <div style={{ fontSize: 12.5, color: theme.textMute, lineHeight: 1.5 }}>
                      Sourced via <strong>{isLinkedIn ? "LinkedIn" : (isGitHub ? "GitHub" : (selectedCandidate?.platform || selectedCandidate?.source || "WIBIL Platform"))}</strong>. Candidate profile authenticated through platform verification.
                    </div>
                  </div>
                  {candidateReport?.profile?.uan && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#10B981", fontWeight: 700 }}>
                      ✓ EPFO / UAN: {candidateReport.profile.uan}
                    </div>
                  )}
                </div>

                <div style={{ padding: "18px 20px", borderRadius: 12, background: theme.bg, border: `1px solid ${theme.line}`, display: "flex", flexDirection: "column" }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: "#10B981", marginBottom: 8 }}>
                    Verified Technical Skills
                  </div>
                  <div style={{ fontSize: 12.5, color: theme.text, lineHeight: 1.55 }}>
                    {selectedCandidate?.strengths || (Array.isArray(selectedCandidate?.skills) ? selectedCandidate.skills.join(", ") : "") || "Full Stack Development, Web Technologies, Software Architecture"}
                  </div>
                </div>
              </div>

              {(() => {
                const scoreObj = {
                  ...(typeof candidateReport?.trust_score === "object" && candidateReport.trust_score ? candidateReport.trust_score : {}),
                  avg_performance: selectedCandidate?.avg_performance ?? candidateReport?.trust_score?.avg_performance,
                  avg_character: selectedCandidate?.avg_character ?? candidateReport?.trust_score?.avg_character,
                  avg_ai_knowledge: selectedCandidate?.avg_ai_knowledge ?? candidateReport?.trust_score?.avg_ai_knowledge,
                  avg_teamwork: selectedCandidate?.avg_teamwork ?? candidateReport?.trust_score?.avg_teamwork,
                  avg_reliability: selectedCandidate?.avg_reliability ?? candidateReport?.trust_score?.avg_reliability,
                  avg_attendance: selectedCandidate?.avg_attendance ?? candidateReport?.trust_score?.avg_attendance,
                  avg_behaviour: selectedCandidate?.avg_behaviour ?? candidateReport?.trust_score?.avg_behaviour,
                  avg_leadership: selectedCandidate?.avg_leadership ?? candidateReport?.trust_score?.avg_leadership
                };
                const hasBreakdown = isWibilRegistered && EMPLOYEE_CRITERIA.some((cr) => Number(scoreObj[cr.avgKey]) > 0);
                if (!hasBreakdown) return null;
                return (
                  <div style={{ marginTop: 16, padding: "16px 18px", borderRadius: 12, background: theme.bg, border: `1px solid ${theme.line}` }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: theme.text, marginBottom: 4 }}>
                      Employer score breakdown
                    </div>
                    <p style={{ fontSize: 12, color: theme.textMute, margin: "0 0 12px" }}>
                      Aggregate ratings given by employers on WIBIL (1–10 per pillar).
                    </p>
                    <BreakdownGrid criteria={EMPLOYEE_CRITERIA} score={scoreObj} theme={theme} />
                  </div>
                );
              })()}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
